/* eslint-disable no-await-in-loop */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */
import { useMemo, useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useReviewManageApiDownloadHandler } from 'Deer/intent/Reviews/hooks/useReviewsIntentElllipsisValues';
import { generateCSVDataApi } from 'api/admin';
import { useToast } from 'components/hooks/useToast';
import { useSelectedView } from 'reducers/viewsSlice';
import { convertToCSVOneOnOne, parseCSVOneOnOne } from 'screens/oneOnOneRabbit/Insights/features/AgGridTable/helper';

import { Download, Refresh2 } from 'assets/assetsV1/iconComponents';

import { TOAST_STATUSES } from 'constants/ProjectConstants';
import { localStorageKeys } from 'constants/localStorageKeys';
import { sendDownloadCSVEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { getEmployeesInfo } from 'services/admin';

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gridRef: any;
};

const localStorageKey = localStorageKeys.adminEmployeesCSVDownloads;

const fetchEmployeesInfo = async () => {
  try {
    const employees = await getEmployeesInfo({ searchText: '', offset: 0, limit: 50000 + 1 });
    return employees;
  } catch (err) {
    console.error('Error fetching the employee list: ', err);
    throw err;
  }
};

export const useGetEmployeeRosterList = () =>
  useQuery([REACT_QUERY_KEYS.settings.EmployeeRoster], () => fetchEmployeesInfo(), {
    enabled: true,
    onError: (error) => error,
  });

export const useGetCSVDownloadOptions = ({ gridRef }: Props) => {
  // using 1:1 insights csv download helper function
  const cohort = useSelectedView();
  const toast = useToast();
  const queryClient = useQueryClient();

  const employeesCurrentViewCSVDownload = useCallback(
    ({ exportedRows }: { exportedRows: 'all' | 'filteredAndSorted' }) => {
      sendDownloadCSVEvent({
        eventProperties: {
          source: 'company settings',
          type: 'employee roster current view download',
        },
      });
      const data = gridRef?.current?.api.getDataAsCsv({
        fileName: 'employees_table.csv',
        allColumns: true,
        processCellCallback(params): string {
          const { value } = params;

          return value;
        },
        exportedRows,
      });

      const parsedCsv = parseCSVOneOnOne(data);

      // Removing Admin access column
      parsedCsv.forEach((row) => {
        Object.keys(row).forEach((key) => {
          if (key.trim().toLowerCase().includes('admin access')) {
            delete row[key];
          }
        });
      });

      const csvData = convertToCSVOneOnOne(parsedCsv);

      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `employees_table ${Date.now()}.csv`;
      a.click();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cohort?.id, cohort?.name, gridRef]
  );

  const refreshAgGridData = useCallback(async () => {
    try {
      await queryClient.invalidateQueries([REACT_QUERY_KEYS.settings.EmployeeRoster]);
    } catch (error) {
      toast({
        title: 'Error reloading. Please try again.',
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
      console.error('Error reloading reviews table data');
    }
  }, [queryClient, toast]);

  const { handleDetailedDownload } = useReviewManageApiDownloadHandler({
    downloadApiHandler: generateCSVDataApi,
    payload: null,
    localStorageKey,
  });

  // eslint-disable-next-line arrow-body-style
  const options: {
    label: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Icon: (props: any) => JSX.Element;
    type: 'subMenu' | 'default' | 'radio' | 'radioSubMenu';
    subOptions?: {
      label: string;
      onClick: () => void;
      description?: string;
    }[];
  }[] = useMemo(
    () => [
      {
        label: 'Refresh employees table',
        Icon: Refresh2,
        onClick: refreshAgGridData,
        type: 'default',
      },
      {
        label: 'Download report',
        Icon: Download,
        type: 'subMenu',
        subOptions: [
          {
            label: 'Current View Download',
            onClick: () => {
              employeesCurrentViewCSVDownload({
                exportedRows: 'filteredAndSorted',
              });
            },
            description: `Download current view description`,
          },
          {
            label: 'Detailed Download',
            onClick: () => {
              handleDetailedDownload(); // download through api for detailed csv
            },
            description: `Download detailed view description`,
          },
        ],
      },
    ],
    [handleDetailedDownload, employeesCurrentViewCSVDownload]
  );

  return options;
};
